import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Container, Row, Col, Button, Table } from 'react-bootstrap';
import MapBrowser from '../track/map';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, ReferenceDot } from 'recharts';

function Device(id, spd, vmg, ang, pos, rotate, lines, main, wind, wds, wdd, all_lines, name, unit, wind_unit,
    high10sSpdSegments, // g.spd >= max_10s_spd
    high10s95perSpdSegments, // g.spd >= max_10s_spd*0.95
    aveSpdSegments,     // g.spd >= ave_speed
    maxSpdSegments      // g.spd >= max_speed
) {
    this.id = id;
    this.spd = spd;
    this.vmg = vmg;
    this.ang = ang;
    this.pos = pos;
    this.rotate = rotate;
    this.all_lines = all_lines;
    this.lines = lines;
    this.coloredLines = lines;
    this.main = main;
    this.wind = wind;
    this.windspd = wds;
    this.winddir = wdd;
    this.isStarboard = true;
    this.name = name;
    this.unit = unit;
    this.wind_unit = wind_unit;
    this.high10sSpdSegments = high10sSpdSegments;
    this.high10s95perSpdSegments = high10s95perSpdSegments;
    this.aveSpdSegments = aveSpdSegments;
    this.maxSpdSegments = maxSpdSegments;
}

class Jibe extends Component {
    constructor(props) {
        super(props);
        this.state = {
            jibes: [],
            minSpeedThreshold: (this.props.authUser && this.props.authUser.jibe_min_speed_threshold) ? this.props.authUser.jibe_min_speed_threshold : 20, // km/h デフォルト
            entrySpeedThreshold: (this.props.authUser && this.props.authUser.jibe_entry_speed_filter) ? this.props.authUser.jibe_entry_speed_filter : 20, // km/h デフォルト
            selectedJibeIndex: null,
            deviceMap: {},
            center: { lat: 35.301318, lng: 139.480219 },
            graphData: [],
            zoom: 18,
            yaxis: false,
            // keys for graph
            dataKeyStb: "spd_s",
            dataKeyPt: "spd_p",
            currentSegmentLines: [],
            markers: [], // for map
            chartReferenceDots: [], // for chart points
            heightLine: null, // for map height loss line
            // Sorting & Filtering
            sortConfig: { key: null, direction: 'ascending' },
            filterTurn: 'All',
            filterResult: 'All'
        };
        this.handleAnalyze = this.handleAnalyze.bind(this);
        this.handleRowClick = this.handleRowClick.bind(this);
        this.handleMouseMove = this.handleMouseMove.bind(this);
        this.handleChartClick = this.handleChartClick.bind(this);
    }

    componentDidMount() {
        // Suppress ResizeObserver loop limit exceeded error
        const originalError = console.error;
        console.error = (...args) => {
            if (typeof args[0] === 'string' && /ResizeObserver loop/.test(args[0])) {
                return;
            }
            originalError.apply(console, args);
        };

        if (this.props.target) {
            this.handleAnalyze();
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.target !== prevProps.target) {
            this.handleAnalyze();
        }
    }

    normalizeDiff(dir, wind) {
        let d = dir - wind;
        if (d > 180) d -= 360;
        if (d < -180) d += 360;
        return d;
    }

    // Helper to calc height metric (proj dist against wind)
    calcHeightMetric(lat, lon, windDir, baseLat, baseLon) {
        const rad = Math.PI / 180;
        const R = 6371000; // earth radius meters
        const x = (lon - baseLon) * rad * R * Math.cos(baseLat * rad);
        const y = (lat - baseLat) * rad * R;

        const windRad = windDir * rad;
        // Upwind vector components (N=0, E=90) -> (sin, cos)
        const ux = Math.sin(windRad);
        const uy = Math.cos(windRad);

        return x * ux + y * uy;
    }

    handleAnalyze() {
        if (!this.props.target || !this.props.target.gpses) return;

        const gpses = this.props.target.gpses;
        const windDir = this.props.target.wind_direction;
        const HOLD_SECONDS = 10;
        const PADDING_SECONDS = 20; // ターン前後の余白秒数
        const minSpeedThreshold = this.state.minSpeedThreshold;
        const entrySpeedThreshold = this.state.entrySpeedThreshold;

        let jibes = [];
        let inTurn = false;
        let turnStartIndex = 0;
        let holdUntilTime = null;
        let prevDiff = null;
        let turnDirection = "";

        for (let i = 0; i < gpses.length; i++) {
            const gps = gpses[i];
            if (!gps.time) continue;

            const curDiff = this.normalizeDiff(gps.direction, windDir);

            if (prevDiff === null) {
                prevDiff = curDiff;
                continue;
            }

            // wind_direction を跨いだか
            const signChanged =
                (prevDiff < 0 && curDiff > 0) ||
                (prevDiff > 0 && curDiff < 0);

            // Jibe検出: 180度/-180度の境界をまたぐ場合、値は大きく飛ぶ (例: 179 -> -179, 差=358)
            // Tack検出: 0度をまたぐ場合、値の変化は小さい (例: -1 -> 1, 差=2)
            // ユーザー要望: 風上でのターン(Tack)を除外し、風下でのターン(Jibe)のみ抽出する
            const crossedWind = signChanged && Math.abs(prevDiff - curDiff) > 180;

            // --- ターン開始 ---
            if (!inTurn && crossedWind) {
                inTurn = true;
                turnStartIndex = i;
                holdUntilTime = gps.time.clone().add(HOLD_SECONDS, "seconds");

                turnDirection =
                    prevDiff < 0 && curDiff > 0
                        ? "Starboard → Port"
                        : "Port → Starboard";
            }

            // --- ターン中 ---
            if (inTurn) {
                // 再クロスしたらキャンセル
                if (crossedWind && i !== turnStartIndex) {
                    inTurn = false;
                    holdUntilTime = null;
                }

                // 10秒維持で確定
                if (holdUntilTime && gps.time.isAfter(holdUntilTime)) {
                    // --- セグメント範囲の決定 (前後20秒含む) ---
                    let startIdx = turnStartIndex;
                    let endIdx = i;

                    // startIdx を20秒前に拡張
                    const startTime = gpses[turnStartIndex].time;
                    const paddedStartTime = startTime.clone().subtract(PADDING_SECONDS, "seconds");
                    while (startIdx > 0 && gpses[startIdx - 1].time && gpses[startIdx - 1].time.isAfter(paddedStartTime)) {
                        startIdx--;
                    }

                    // endIdx を20秒後に拡張
                    const endTime = gpses[endIdx].time;
                    const paddedEndTime = endTime.clone().add(PADDING_SECONDS, "seconds");
                    while (endIdx < gpses.length - 1 && gpses[endIdx + 1].time && gpses[endIdx + 1].time.isBefore(paddedEndTime)) {
                        endIdx++;
                    }

                    // 表示用セグメント (拡張版)
                    const extendedSegment = gpses.slice(startIdx, endIdx + 1);

                    // 分析用コアセグメント (元のロジック通り、ターン開始〜10秒後まで)
                    const coreSegment = gpses.slice(turnStartIndex, i);

                    // --- 数値計算とポイント特定 ---
                    // Entry Speed Point
                    const entrySegment = gpses.slice(startIdx, turnStartIndex + 1);
                    let maxSpeedPt = null;
                    let maxSpeed = -1;

                    if (entrySegment.length > 0) {
                        entrySegment.forEach(g => {
                            const s = g._speed ?? g.speed ?? 0;
                            if (s >= maxSpeed) {
                                maxSpeed = s;
                                maxSpeedPt = g;
                            }
                        });
                    }

                    // Min Speed Point
                    let minSpeedPt = null;
                    let minSpeed = Infinity;

                    if (coreSegment.length > 0) {
                        coreSegment.forEach(g => {
                            const s = g._speed ?? g.speed ?? 0;
                            if (s < minSpeed) {
                                minSpeed = s;
                                minSpeedPt = g;
                            }
                        });
                    }

                    // Fallback
                    if (maxSpeed === -1) maxSpeed = 0;
                    if (minSpeed === Infinity) minSpeed = 0;

                    // Elapsed Time: Duration between Entry Speed Point (Max) and Min Speed Point
                    let elapsedTime = minSpeedPt && maxSpeedPt ? minSpeedPt.time.diff(maxSpeedPt.time) / 1000 : 0;

                    // --- Height Loss Calculation (Intersect Method) ---
                    const baseLat = extendedSegment[0].lat;
                    const baseLon = extendedSegment[0].lon;

                    // 1. Find Highest Point (Most Upwind) -> CHANGED: Use Entry Speed Point (maxSpeedPt)
                    // User request: "もっとも風の高さがある位置からの高さ表示になっているので、クリックしたときと同じ仕様にしてください。"
                    // This means we should start the height line from the max speed point, not necessarily the highest upwind point.
                    let pHigh = maxSpeedPt;
                    let pHighIndex = -1;

                    if (pHigh) {
                        pHighIndex = extendedSegment.indexOf(pHigh);
                    }

                    // 2. Calculate Downwind Ray
                    const rad = Math.PI / 180;
                    const downwindRad = (windDir + 180) * rad;
                    const vx = Math.sin(downwindRad);
                    const vy = Math.cos(downwindRad);

                    // XY conversion consts
                    const R = 6371000;
                    const ky = rad * R;
                    const kx = rad * R * Math.cos(baseLat * rad);

                    const toXY = (p) => ({
                        x: (p.lon - baseLon) * kx,
                        y: (p.lat - baseLat) * ky
                    });

                    const A = toXY(pHigh); // Ray Origin

                    let pCross = null;
                    let heightDiffVal = 0;
                    let bestT = Infinity;

                    // 3. Find Intersection with Track Segments *after* pHigh
                    if (pHighIndex !== -1) {
                        for (let k = pHighIndex + 1; k < extendedSegment.length - 1; k++) {
                            const C = toXY(extendedSegment[k]);
                            const D = toXY(extendedSegment[k + 1]);

                            // Solve: A + t*V = C + s*(D-C)
                            const dx_seg = D.x - C.x;
                            const dy_seg = D.y - C.y;

                            // Linear system:
                            // | vx  -dx_seg | | t | = | C.x - A.x |
                            // | vy  -dy_seg | | s |   | C.y - A.y |

                            const det = vx * (-dy_seg) - vy * (-dx_seg);
                            if (Math.abs(det) < 1e-9) continue; // Parallel

                            const diffX = C.x - A.x;
                            const diffY = C.y - A.y;

                            const t = (diffX * (-dy_seg) - diffY * (-dx_seg)) / det;
                            const s = (vx * diffY - vy * diffX) / det;

                            if (t > 1.0 && s >= 0 && s <= 1) { // t > 1m to avoid self-intersection or very close noise
                                if (t < bestT) {
                                    bestT = t;
                                    // Calc intersection point
                                    const ix = A.x + t * vx;
                                    const iy = A.y + t * vy;

                                    // Convert back to LatLon
                                    pCross = {
                                        lat: (iy / ky) + baseLat,
                                        lon: (ix / kx) + baseLon
                                    };
                                }
                            }
                        }
                    }

                    let heightDiffStr = "-";
                    if (pCross) {
                        heightDiffVal = bestT;
                        heightDiffStr = heightDiffVal.toFixed(0);
                    }

                    const result =
                        minSpeed < minSpeedThreshold ? "Fail" : "Success";

                    let entryAngle = "-";
                    if (maxSpeedPt) {
                        entryAngle = (90 - Math.abs(this.normalizeDiff(maxSpeedPt.direction, windDir))).toFixed(0);
                    }

                    if (maxSpeed >= entrySpeedThreshold) {
                        jibes.push({
                            originalIndex: jibes.length,
                            turnDirection,
                            entrySpeed: maxSpeed.toFixed(2),
                            entryAngle,
                            minSpeed: minSpeed.toFixed(2),
                            elapsedTime: elapsedTime.toFixed(1),
                            heightDiff: heightDiffStr,
                            result,
                            segment: extendedSegment,
                            maxSpeedPt,
                            minSpeedPt,
                            pHigh,
                            pCross
                        });
                    }

                    inTurn = false;
                    holdUntilTime = null;
                }
            }

            prevDiff = curDiff;
        }

        // Auto-select first jibe if available
        const firstJibe = jibes.length > 0 ? jibes[0] : null;

        this.setState({
            jibes,
            selectedJibeIndex: null,
            deviceMap: {},
            graphData: [],
            currentSegmentLines: [],
            markers: [],
            chartReferenceDots: [],
            heightLine: null
        }, () => {
            if (firstJibe) {
                this.handleRowClick(firstJibe);
            }
        });
    }

    requestSort(key) {
        let direction = 'ascending';
        if (this.state.sortConfig.key === key && this.state.sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        this.setState({ sortConfig: { key, direction } });
    }

    getProcessedJibes() {
        let jibes = [...this.state.jibes];

        // Filtering
        if (this.state.filterTurn !== 'All') {
            if (this.state.filterTurn === 'Starboard') {
                jibes = jibes.filter(j => j.turnDirection.startsWith('Starboard'));
            } else if (this.state.filterTurn === 'Port') {
                jibes = jibes.filter(j => j.turnDirection.startsWith('Port'));
            }
        }
        if (this.state.filterResult !== 'All') {
            jibes = jibes.filter(j => j.result === this.state.filterResult);
        }

        // Sorting
        if (this.state.sortConfig.key !== null) {
            jibes.sort((a, b) => {
                let aVal = a[this.state.sortConfig.key];
                let bVal = b[this.state.sortConfig.key];

                // Numeric convert if needed
                if (['entrySpeed', 'entryAngle', 'minSpeed', 'elapsedTime'].includes(this.state.sortConfig.key)) {
                    aVal = parseFloat(aVal);
                    bVal = parseFloat(bVal);
                }
                if (this.state.sortConfig.key === 'heightDiff') {
                    aVal = aVal === '-' ? -999 : parseFloat(aVal);
                    bVal = bVal === '-' ? -999 : parseFloat(bVal);
                }

                if (aVal < bVal) return this.state.sortConfig.direction === 'ascending' ? -1 : 1;
                if (aVal > bVal) return this.state.sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return jibes;
    }

    handleRowClick(jibe) {
        if (!jibe || !jibe.segment || jibe.segment.length === 0) return;


        const segment = jibe.segment;
        const windDir = this.props.target.wind_direction;

        // --- Graph Data Prep ---
        const graphData = segment.map(p => {
            const speed = (p._speed || p.speed || 0);
            const diff = this.normalizeDiff(p.direction, windDir);

            return {
                time: p.time.format("HH:mm:ss"),
                spd_s: diff < 0 ? speed : null,
                spd_p: diff >= 0 ? speed : null,
                rawSpeed: speed,
                lat: p.lat,
                lon: p.lon,
                direction: p.direction,
                speedObj: p
            };
        });

        // --- Markers & Dots ---
        const markers = [];
        const chartReferenceDots = [];

        // Max Speed Marker
        if (jibe.maxSpeedPt) {
            const val = jibe.maxSpeedPt._speed ?? jibe.maxSpeedPt.speed ?? 0;
            markers.push({
                lat: jibe.maxSpeedPt.lat,
                lon: jibe.maxSpeedPt.lon,
                color: '#00BFFF', // DeepSkyBlue
                label: 'Max',
                value: val.toFixed(2) + " km/h"
            });
            chartReferenceDots.push({
                time: jibe.maxSpeedPt.time.format("HH:mm:ss"),
                speed: val,
                r: 6,
                fill: '#00BFFF',
                stroke: 'white',
                strokeWidth: 2
            });
        }

        // Min Speed Marker
        if (jibe.minSpeedPt) {
            const val = jibe.minSpeedPt._speed ?? jibe.minSpeedPt.speed ?? 0;
            markers.push({
                lat: jibe.minSpeedPt.lat,
                lon: jibe.minSpeedPt.lon,
                color: '#FF4500', // OrangeRed
                label: 'Min',
                value: val.toFixed(2) + " km/h"
            });
            chartReferenceDots.push({
                time: jibe.minSpeedPt.time.format("HH:mm:ss"),
                speed: val,
                r: 6,
                fill: '#FF4500',
                stroke: 'white',
                strokeWidth: 2
            });
        }

        // --- Height Loss Line ---
        let heightLine = null;
        if (jibe.pHigh && jibe.pCross && jibe.heightDiff !== "-") {
            heightLine = {
                pos: [[jibe.pHigh.lat, jibe.pHigh.lon], [jibe.pCross.lat, jibe.pCross.lon]],
                text: jibe.heightDiff + "m"
            };
        }

        // --- Map Line ---
        const gpsLine = segment.map(p => {
            const diff = this.normalizeDiff(p.direction, windDir);
            return {
                pos: [p.lat, p.lon],
                isStb: diff < 0
            };
        });

        let latSum = 0, lonSum = 0;
        gpsLine.forEach(p => { latSum += p.pos[0]; lonSum += p.pos[1]; });
        const center = { lat: latSum / gpsLine.length, lng: lonSum / gpsLine.length };

        const firstP = graphData[0];
        const device = new Device(
            this.props.target.user.id,
            firstP.rawSpeed,
            0, firstP.direction || 0,
            { lat: firstP.lat, lng: firstP.lon },
            firstP.direction || 0,
            gpsLine, true, false, 0, 0, gpsLine,
            this.props.target.user.last_name,
            this.props.target.user.unit || "km/h",
            this.props.target.user.wind_unit || "m/s",
            [], [], [], []
        );

        const deviceMap = {};
        deviceMap[this.props.target.user.id] = device;

        this.setState({
            selectedJibeIndex: jibe.originalIndex,
            graphData,
            deviceMap,
            center,
            currentSegmentLines: gpsLine,
            markers,
            chartReferenceDots,
            heightLine
        });
    }

    handleMouseMove(e) {
        if (!e || !e.activePayload || !e.activePayload.length) return;

        const data = e.activePayload[0].payload;
        if (!data) return;

        const device = new Device(
            this.props.target.user.id,
            data.rawSpeed,
            0,
            data.direction,
            { lat: data.lat, lng: data.lon },
            data.direction,
            this.state.currentSegmentLines,
            true,
            false,
            0, 0,
            this.state.currentSegmentLines,
            this.props.target.user.last_name,
            this.props.target.user.unit || "km/h",
            this.props.target.user.wind_unit || "m/s",
            [], [], [], []
        );

        const deviceMap = {};
        deviceMap[this.props.target.user.id] = device;

        this.setState({ deviceMap });
    }

    handleChartClick(e) {
        if (!e || !e.activePayload || !e.activePayload.length) return;
        const clickedPt = e.activePayload[0].payload.speedObj;
        if (!clickedPt) return;

        if (this.state.selectedJibeIndex !== null) {
            this.updateJibeStats(this.state.selectedJibeIndex, clickedPt);
        }
    }

    updateJibeStats(originalIndex, newMaxSpeedPt) {
        const jibes = [...this.state.jibes];
        const jibeIndex = jibes.findIndex(j => j.originalIndex === originalIndex);
        if (jibeIndex === -1) return;

        const jibe = jibes[jibeIndex];
        const windDir = this.props.target.wind_direction;
        const minSpeedPt = jibe.minSpeedPt;
        const segment = jibe.segment;

        // 1. Update Entry Speed
        const entrySpeed = (newMaxSpeedPt._speed || newMaxSpeedPt.speed || 0);

        // 2. Update Entry Angle
        const entryAngle = (90 - Math.abs(this.normalizeDiff(newMaxSpeedPt.direction, windDir))).toFixed(0);

        // 3. Update Elapsed Time
        let elapsedTime = 0;
        if (minSpeedPt) {
            elapsedTime = minSpeedPt.time.diff(newMaxSpeedPt.time) / 1000;
        }

        // 4. Update Height Loss
        // Use newMaxSpeedPt as pHigh
        const pHigh = newMaxSpeedPt;
        const baseLat = segment[0].lat;
        const baseLon = segment[0].lon;

        let pHighIndex = -1;
        for (let i = 0; i < segment.length; i++) {
            if (segment[i] === pHigh) {
                pHighIndex = i;
                break;
            }
        }

        // Intersection Math
        const rad = Math.PI / 180;
        // Jibe Downwind Ray
        const downwindRad = (windDir + 180) * rad;
        const vx = Math.sin(downwindRad);
        const vy = Math.cos(downwindRad);

        const R = 6371000;
        const ky = rad * R;
        const kx = rad * R * Math.cos(baseLat * rad);

        const toXY = (p) => ({
            x: (p.lon - baseLon) * kx,
            y: (p.lat - baseLat) * ky
        });

        const A = toXY(pHigh);

        let pCross = null;
        let heightDiffVal = 0;
        let bestT = Infinity;

        if (pHighIndex !== -1) {
            for (let k = pHighIndex + 1; k < segment.length - 1; k++) {
                const C = toXY(segment[k]);
                const D = toXY(segment[k + 1]);

                const dx_seg = D.x - C.x;
                const dy_seg = D.y - C.y;

                const det = vx * (-dy_seg) - vy * (-dx_seg);
                if (Math.abs(det) < 1e-9) continue;

                const diffX = C.x - A.x;
                const diffY = C.y - A.y;

                const t = (diffX * (-dy_seg) - diffY * (-dx_seg)) / det;
                const s = (vx * diffY - vy * diffX) / det;

                if (t > 1.0 && s >= 0 && s <= 1) {
                    if (t < bestT) {
                        bestT = t;
                        const ix = A.x + t * vx;
                        const iy = A.y + t * vy;
                        pCross = {
                            lat: (iy / ky) + baseLat,
                            lon: (ix / kx) + baseLon
                        };
                    }
                }
            }
        }

        let heightDiffStr = "-";
        if (pCross) {
            heightDiffVal = bestT;
            heightDiffStr = heightDiffVal.toFixed(0);
        }

        const updatedJibe = {
            ...jibe,
            entrySpeed: entrySpeed.toFixed(2),
            entryAngle,
            elapsedTime: elapsedTime.toFixed(1),
            heightDiff: heightDiffStr,
            maxSpeedPt: newMaxSpeedPt,
            pHigh: pHigh,
            pCross: pCross
        };

        jibes[jibeIndex] = updatedJibe;
        this.setState({ jibes }, () => {
            this.handleRowClick(updatedJibe);
        });
    }

    render() {
        // Calculate Stats
        const processedJibes = this.getProcessedJibes();
        const totalJibes = processedJibes.length;
        const successCount = processedJibes.filter(j => j.result === "Success").length;
        const failCount = totalJibes - successCount;
        const successRate = totalJibes > 0 ? ((successCount / totalJibes) * 100).toFixed(1) : "0.0";

        return (
            <Container fluid style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
                {/* Top Half: Settings & Jibe List */}
                <div style={{ flex: 1, overflow: 'auto', borderBottom: '1px solid #444', minHeight: 0 }}>
                    <Row className="mb-3" style={{ marginTop: '20px' }}>
                        <Col md={3}>
                            <label>Min Speed Threshold</label>
                            <input
                                type="number"
                                className="form-control"
                                value={this.state.minSpeedThreshold}
                                onChange={(e) =>
                                    this.setState({
                                        minSpeedThreshold: Number(e.target.value)
                                    }, this.handleAnalyze)
                                }
                            />
                        </Col>
                        <Col md={3}>
                            <label>Entry Speed Filter</label>
                            <input
                                type="number"
                                className="form-control"
                                value={this.state.entrySpeedThreshold}
                                onChange={(e) =>
                                    this.setState({
                                        entrySpeedThreshold: Number(e.target.value)
                                    }, this.handleAnalyze)
                                }
                            />
                        </Col>
                        <Col md={2}>
                            <label>Filter Turn</label>
                            <select
                                className="form-control"
                                value={this.state.filterTurn}
                                onChange={(e) => this.setState({ filterTurn: e.target.value })}
                            >
                                <option value="All">All</option>
                                <option value="Starboard">Starboard</option>
                                <option value="Port">Port</option>
                            </select>
                        </Col>
                        <Col md={2}>
                            <label>Filter Result</label>
                            <select
                                className="form-control"
                                value={this.state.filterResult}
                                onChange={(e) => this.setState({ filterResult: e.target.value }, this.handleAnalyze)}
                            >
                                <option value="All">All</option>
                                <option value="Success">Success</option>
                                <option value="Fail">Fail</option>
                            </select>
                        </Col>
                    </Row>

                    {/* Stats Row */}
                    <Row className="mb-2" style={{ color: 'black' }}>
                        <Col>
                            <span style={{ fontSize: '1.1em', marginRight: '20px' }}>
                                <strong>Total:</strong> {totalJibes}
                            </span>
                            <span style={{ fontSize: '1.1em', marginRight: '20px' }}>
                                <strong>Success Rate:</strong> {successRate}%
                            </span>
                            <span style={{ color: 'green', marginRight: '10px' }}>
                                (Success: {successCount})
                            </span>
                            <span style={{ color: 'red' }}>
                                (Fail: {failCount})
                            </span>
                        </Col>
                    </Row>

                    <Row>
                        <Col>
                            <Table striped bordered hover variant="dark">
                                <thead>
                                    <tr>
                                        <th>No.</th>
                                        <th onClick={() => this.requestSort('turnDirection')} style={{ cursor: 'pointer' }}>Turn {this.state.sortConfig.key === 'turnDirection' ? (this.state.sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}</th>
                                        <th onClick={() => this.requestSort('entrySpeed')} style={{ cursor: 'pointer' }}>Entry Speed {this.state.sortConfig.key === 'entrySpeed' ? (this.state.sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}</th>
                                        <th onClick={() => this.requestSort('entryAngle')} style={{ cursor: 'pointer' }}>Entry Angle {this.state.sortConfig.key === 'entryAngle' ? (this.state.sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}</th>
                                        <th onClick={() => this.requestSort('minSpeed')} style={{ cursor: 'pointer' }}>Min Speed {this.state.sortConfig.key === 'minSpeed' ? (this.state.sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}</th>
                                        <th onClick={() => this.requestSort('heightDiff')} style={{ cursor: 'pointer' }}>Height Loss (m) {this.state.sortConfig.key === 'heightDiff' ? (this.state.sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}</th>
                                        <th onClick={() => this.requestSort('elapsedTime')} style={{ cursor: 'pointer' }}>Elapsed Time {this.state.sortConfig.key === 'elapsedTime' ? (this.state.sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}</th>
                                        <th onClick={() => this.requestSort('result')} style={{ cursor: 'pointer' }}>Result {this.state.sortConfig.key === 'result' ? (this.state.sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {processedJibes.map((jibe, index) => {
                                        const isSelected = this.state.selectedJibeIndex === jibe.originalIndex;
                                        const isStarboard = jibe.turnDirection.startsWith("Starboard");
                                        const isSuccess = jibe.result === "Success";

                                        // Background based on Result (Success/Fail)
                                        const bgColor = isSuccess ? '#1b451b' : '#451b1b';

                                        // Box indicator color based on Turn Direction
                                        const turnIndicatorColor = isStarboard ? 'green' : 'red';

                                        return (
                                            <tr
                                                key={jibe.originalIndex}
                                                onClick={() => this.handleRowClick(jibe)}
                                                style={{
                                                    cursor: 'pointer',
                                                    backgroundColor: bgColor
                                                }}
                                            >
                                                <td>
                                                    {isSelected && <span style={{ color: '#FFD700', fontWeight: 'bold', marginRight: '5px' }}>✓</span>}
                                                    {index + 1}
                                                </td>
                                                <td>
                                                    <span style={{ color: turnIndicatorColor, marginRight: '8px' }}>■</span>
                                                    {jibe.turnDirection}
                                                </td>
                                                <td>{jibe.entrySpeed}</td>
                                                <td>{jibe.entryAngle}</td>
                                                <td>{jibe.minSpeed}</td>
                                                <td>{jibe.heightDiff}</td>
                                                <td>{jibe.elapsedTime}</td>
                                                <td
                                                    style={{
                                                        color: !isSuccess ? "salmon" : "lightgreen",
                                                        fontWeight: "bold"
                                                    }}
                                                >
                                                    {jibe.result}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {processedJibes.length === 0 && (
                                        <tr>
                                            <td colSpan="8" className="text-center">
                                                No jibes analysis data
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </Col>
                    </Row>
                </div>

                {/* Bottom Half: Map & Graph */}
                <div style={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
                    {/* Left: Map */}
                    <div style={{ flex: 1, borderRight: '1px solid #444', position: 'relative', overflow: 'hidden' }}>
                        <MapBrowser
                            zoom={this.state.zoom}
                            deviceMap={this.state.deviceMap}
                            center={this.state.center}
                            windSpeed={0}
                            windDirection={this.props.target ? this.props.target.wind_direction : 0}
                            windpos={{ lat: 0, lng: 0 }}
                            buoy_coordinate={[]}
                            authUser={this.props.authUser}
                            calcLine={[]}
                            markers={this.state.markers}
                            heightLine={this.state.heightLine}
                        />
                    </div>

                    {/* Right: Graph */}
                    <div style={{ flex: 1, padding: '10px', minWidth: 0, position: 'relative' }}>
                        <div style={{ position: 'absolute', top: 10, bottom: 10, left: 10, right: 10 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={this.state.graphData} onMouseMove={this.handleMouseMove} onClick={this.handleChartClick}>
                                    <CartesianGrid stroke="#eee" strokeDasharray="5 5" />
                                    <XAxis dataKey="time" stroke="#ccc" />
                                    <YAxis reversed={this.state.yaxis} stroke="#ccc" />
                                    <Tooltip
                                        contentStyle={{ backgroundColor: '#222', border: '1px solid #444' }}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="spd_s"
                                        stroke="#006400"
                                        isAnimationActive={false}
                                        dot={false}
                                        connectNulls={true}
                                        strokeWidth={2}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="spd_p"
                                        stroke="#dc143c"
                                        isAnimationActive={false}
                                        dot={false}
                                        connectNulls={true}
                                        strokeWidth={2}
                                    />
                                    {this.state.chartReferenceDots.map((dot, idx) => (
                                        <ReferenceDot
                                            key={idx}
                                            x={dot.time}
                                            y={dot.speed}
                                            r={dot.r}
                                            fill={dot.fill}
                                            stroke={dot.stroke}
                                            strokeWidth={dot.strokeWidth}
                                        />
                                    ))}
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                        {this.state.graphData.length === 0 && (
                            <div style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                color: '#aaa'
                            }}>
                                Select a jibe to view details
                            </div>
                        )}
                    </div>
                </div>
            </Container>
        );
    }
}

const mapStateToProps = state => {
    return {
        target: state.PlayerSelector.target,
        gpsLog: state.TeamList.gpsLog,
        authUser: state.auth.user,
    };
};

export default connect(mapStateToProps)(Jibe);
